import { BadgeIndianRupee, Check, Package, Printer } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  printSaleBill,
  SaleBill,
  SaleBillPrintCopy,
  type SaleBillProps,
} from '@/shared/ui';
import { formatDate } from '@/shared/utils/date';

import type { OfflineSaleBill } from '../types';

export interface OfflineSaleReceiptDialogProps {
  receipt: OfflineSaleBill | null;
  onNewSale: () => void;
}

export function OfflineSaleReceiptDialog({ receipt, onNewSale }: OfflineSaleReceiptDialogProps) {
  const { t } = useTranslation('offlineOrders');
  const open = receipt !== null;
  const homeDelivery = receipt?.fulfillmentType === 'HOME_DELIVERY';
  const billProps = receipt ? saleBillProps(receipt, t) : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onNewSale();
      }}
    >
      <DialogContent
        className="flex max-h-[min(44rem,calc(100vh-2rem))] w-[calc(100vw-2rem)] max-w-lg flex-col overflow-hidden p-0"
        showClose
      >
        {billProps ? (
          <>
            <div
              className={
                homeDelivery
                  ? 'border-b border-border bg-info-subtle px-5 py-4 pr-14'
                  : 'border-b border-border bg-success-subtle px-5 py-4 pr-14'
              }
            >
              <div className="flex items-start gap-3">
                <span
                  className={
                    homeDelivery
                      ? 'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface text-info-strong'
                      : 'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface text-success-strong'
                  }
                >
                  {homeDelivery ? (
                    <Package aria-hidden="true" className="h-5 w-5" strokeWidth={2.4} />
                  ) : (
                    <Check aria-hidden="true" className="h-5 w-5" strokeWidth={2.4} />
                  )}
                </span>
                <div className="min-w-0">
                  <DialogTitle>
                    {homeDelivery
                      ? t('checkout.homeDelivery.receiptTitle')
                      : t('checkout.receiptTitle')}
                  </DialogTitle>
                  <p className="mt-1 text-sm text-fg-muted">
                    {homeDelivery ? t('checkout.homeDelivery.thanks') : t('checkout.thanks')}
                  </p>
                </div>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <SaleBill {...billProps} />
            </div>

            <div className="flex flex-col gap-2 border-t border-border px-5 py-4 sm:flex-row">
              <Button type="button" variant="secondary" className="flex-1" onClick={printSaleBill}>
                <Printer aria-hidden="true" className="h-4 w-4" strokeWidth={2} />
                {t('checkout.print')}
              </Button>
              <Button type="button" className="flex-1" onClick={onNewSale}>
                {t('checkout.newSale')}
              </Button>
            </div>
          </>
        ) : null}
      </DialogContent>
      {billProps ? <SaleBillPrintCopy {...billProps} /> : null}
    </Dialog>
  );
}

function saleBillProps(
  bill: OfflineSaleBill,
  t: (key: string, options?: Record<string, unknown>) => string,
): Omit<SaleBillProps, 'printCopy'> {
  const homeDelivery = bill.fulfillmentType === 'HOME_DELIVERY';
  const storeLabel = [bill.storeCode, bill.storeName].filter(Boolean).join(' · ');
  const rows = [
    { label: t('cart.subtotal'), amountPaise: bill.subtotalPaise },
    ...(bill.discountPaise > 0
      ? [{ label: t('cart.discount'), amountPaise: bill.discountPaise, savings: true }]
      : []),
    ...(bill.couponDiscountPaise > 0
      ? [
          {
            label: bill.couponCode
              ? t('checkout.couponWithCode', { code: bill.couponCode })
              : t('cart.coupon'),
            amountPaise: bill.couponDiscountPaise,
            savings: true,
          },
        ]
      : []),
    ...(bill.handlingChargePaise > 0
      ? [{ label: t('cart.handling'), amountPaise: bill.handlingChargePaise }]
      : []),
    ...(bill.deliveryChargePaise > 0
      ? [{ label: t('cart.delivery'), amountPaise: bill.deliveryChargePaise }]
      : []),
  ];

  return {
    brand: t('checkout.brand'),
    subtitle: homeDelivery ? t('checkout.homeDelivery.cashSale') : t('checkout.cashSale'),
    storeLabel: storeLabel || undefined,
    storeCity: bill.storeCity || undefined,
    soldAtLabel: bill.soldAt ? formatDate(bill.soldAt, 'dateTime') : undefined,
    orderNumber: bill.orderNumber || bill.orderId,
    orderId: bill.orderId,
    pills: homeDelivery
      ? [
          { label: t('checkout.homeDelivery.picking'), tone: 'info', icon: Package },
          {
            label: t('checkout.homeDelivery.pendingCollection'),
            tone: 'warning',
            icon: BadgeIndianRupee,
          },
        ]
      : [
          { label: t('checkout.delivered'), tone: 'success', icon: Check },
          { label: t('checkout.paymentCash'), tone: 'success', icon: BadgeIndianRupee },
        ],
    customerLabel: t('checkout.customer'),
    customerName: bill.customerName || undefined,
    customerPhone: bill.customerPhone || undefined,
    customerAddressLines: bill.addressLines,
    emptyCustomerLabel: t('checkout.walkIn'),
    itemsLabel: t('checkout.items'),
    formatLineMeta: (quantity) => t('checkout.lineMeta', { quantity }),
    lines: (bill.lines ?? []).map((line) => ({
      id: line.productId,
      name: line.name,
      quantity: line.quantity,
      unitPricePaise: line.unitPricePaise,
      lineTotalPaise: line.lineTotalPaise,
      mrpPaise: line.mrpPaise,
    })),
    rows,
    payableLabel: t('cart.payable'),
    payablePaise: bill.totalAmountPaise,
    paidLabel: homeDelivery ? undefined : t('checkout.cashReceived'),
    paidPaise: homeDelivery ? undefined : bill.totalAmountPaise,
    note: homeDelivery ? t('checkout.homeDelivery.deliveryFeeNote') : undefined,
  };
}
